/* eslint-disable jsx-a11y/anchor-is-valid */
// @flow
import React, {useCallback, useEffect, useRef} from "react";
import PriceOverviewContainer from "../../../container/checkout/dataSummaryBox/PriceOverviewContainer";
import CartItemOverviewContainer from "../../../container/checkout/mainContentBox/OrderSummaryCartItemContainer";
import type {Item} from "../../../models/Item";
import type {Adress} from "../../../models/Adress";
import {isPayPalProvider, PAYMENT_METHODS} from "../../../models/PaymentRequests";
import "./OrderSummary.scss";
import PickupLocationInfoContainer from "../../../container/pickupLocation/PickupLocationInfoContainer";
import Modal from "react-modal";
import type {PaymentFacility, PaymentMethod} from "../../../api/PaymentApi";
import InlineNotice from "../../layout/InlineNotice";
import NextButton from "../NextButton";
import {OrderState} from "../../../models/Order";
import Loading from "../../layout/Loading";
import {useTranslation} from "react-i18next";
import P24PayPalButtonContainer from "../../../container/checkout/dataSummaryBox/P24PayPalButtonContainer";
import PayPalButtonContainer from "../../../container/checkout/dataSummaryBox/PayPalButtonContainer";
import usePayPalScript from "../../../hooks/usePayPalScript";
import {emitInternalEvent} from "../../../helper/tracker/internalEvents";
import WarrantyNoticeModal from "../../empco/WarrantyNoticeModal";
import {Check, SquarePen, X} from "lucide-react";
import Icon from "../../icons/Icon";
import classNames from "classnames";

interface OrderSummaryProps {
	items: Item[];
    adress: Adress;
	shippingText: string;
	withPickup: boolean;
	showPickupLocation: boolean;
	legalTerms: string;
	dealerId: string;
	notificationUrl: string;
	paymentFacility: PaymentFacility;
    paymentMethods: PaymentMethod[];
    paymentErrors: {};
    isAllShipping: boolean;
    isAssigningPayment: boolean;
    orderIsCreated: boolean;
    orderPayedFullyByCoupon: boolean;
    paymentAuthorizationFailed: boolean;
    orderState: OrderState;
    orderNo: string;
    paymentVerified: boolean;
    getOrderState: (orderNo: string) => void;
    assignPaymentMethod: (method: PaymentMethod) => void;
    setOrderFullyPayedByCoupon: () => void;
    editBillingAddress: () => void;
    submitOrder: () => void;
    euShop: boolean;
}

// what the customer learns of the chosen payment before paying
const paymentMethodHint = paymentMethod => {
    switch (paymentMethod) {
        case PAYMENT_METHODS.PREPAID:
            return "cart.processHint.step3.prepaid";
        case PAYMENT_METHODS.CASH:
            return "cart.processHint.step3.cash";
        default:
            return null;
    }
};

/**
 * "Review and pay": the order to check, the payment method to choose and the consent to give, on one
 * page - what were the checkout's payment and overview steps. The pay button stands from the start;
 * pressed with something missing it says what, instead of not being there (#57041).
 */
const OrderSummary = (props: OrderSummaryProps) => {
    const {
        paymentAuthorizationFailed,
        adress,
        orderState,
        items,
        shippingText,
        showPickupLocation,
        withPickup,
        paymentFacility,
        paymentMethods,
        paymentErrors,
        isAllShipping,
        isAssigningPayment,
        orderIsCreated,
        orderPayedFullyByCoupon,
        setOrderFullyPayedByCoupon,
        assignPaymentMethod,
        legalTerms,
        notificationUrl,
        dealerId,
        orderNo,
        paymentVerified,
        getOrderState,
        submitOrder,
        euShop
    } = props;
    const {t} = useTranslation();
    const [accept, setAccept] = React.useState(false);
    const [chosenMethod, setChosenMethod] = React.useState(null);
    const [consentMissing, setConsentMissing] = React.useState(false);
    const [paymentMissing, setPaymentMissing] = React.useState(false);
    const [showLegalTerms, setShowLegalTerms] = React.useState(false);
    const [showWarrantyNotice, setShowWarrantyNotice] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(paymentAuthorizationFailed);
    const consentElement = useRef(null);
    const paymentElement = useRef(null);

    const {getFundingSource, isPaymentMethodAvailable} = usePayPalScript("capture");

    // a coupon that pays all of the order leaves nothing to choose
    useEffect(() => {
        if (orderPayedFullyByCoupon) {
            setOrderFullyPayedByCoupon();
        }
    }, [orderPayedFullyByCoupon, setOrderFullyPayedByCoupon]);

    // excluding paypal express
    const isPayPalPayment = useCallback(() => {
        return paymentFacility.paymentMethod === PAYMENT_METHODS.PAYPAL ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.CARD ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.SEPA ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.PRZELEWY24;
    }, [paymentFacility.paymentMethod]);

    const isPayPalExpressPayment = () => {
        return paymentFacility.paymentMethod === PAYMENT_METHODS.PAYPAL_EXPRESS;
    }

    // paid already (PayPal Express from the cart) or by the coupon: no payment method to choose
    const isPaymentFixed = isPayPalExpressPayment() || paymentFacility.paymentMethod === PAYMENT_METHODS.FREE;

    const isOrderPending = useCallback(() => {
        return orderNo && paymentVerified && orderState !== OrderState.AUTHORIZED;
    }, [orderNo, paymentVerified, orderState]);

    const isOrderReadyToSubmit = useCallback(() => {
        return orderNo && !!paymentVerified && orderState === OrderState.AUTHORIZED;
    }, [orderNo, paymentVerified, orderState]);

    // for PayPal: poll order state, wait for order being updated after successful payment authorization
    useEffect(() => {
        if (isOrderPending()) {
            const interval = setInterval(() => {
                getOrderState(orderNo);
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [paymentFacility, paymentVerified, orderState, orderNo, getOrderState, isOrderPending]);

    // for PayPal: automatically submit the order if payment and order are authorized
    useEffect(() => {
        if(isPayPalPayment() && isOrderReadyToSubmit()) {
            submitOrder();
        }
    }, [paymentFacility, paymentVerified, orderState, orderNo, submitOrder, isPayPalPayment, isOrderReadyToSubmit]);

    const paymentHint = (paymentMethod) => {
		switch (paymentMethod) {
            case PAYMENT_METHODS.FREE:
			case PAYMENT_METHODS.PREPAID:
			case PAYMENT_METHODS.CASH:
			case PAYMENT_METHODS.PAY_LATER:
				return "cart.processHint.step4.withoutAuthorization";
			default:
				return "cart.processHint.step4.withAuthorization";
		}
	};

	const toggleAGB = () => {
        if (!showLegalTerms) emitInternalEvent("text_terms_view");
        setShowLegalTerms(!showLegalTerms);
	};

    const toggleWarrantyNotice = () => {
        if (!showWarrantyNotice) emitInternalEvent("text_warranty_view");
        setShowWarrantyNotice(!showWarrantyNotice);
    };

    const toggleAccept = () => {
        if (!accept) setConsentMissing(false);
        setAccept(!accept);
    };

    const choosePaymentMethod = (method: PaymentMethod) => {
        setChosenMethod(method.paymentMethod);
        setPaymentMissing(false);
        assignPaymentMethod(method);
    };

    // The PayPal buttons ask this on their click - with the latest consent, not the one of the
    // render they were made in.
    const acceptRef = useRef(accept);
    acceptRef.current = accept;
    const paymentRef = useRef(paymentFacility);
    paymentRef.current = paymentFacility;

    const canPay = useCallback(() => {
        const withoutPayment = !paymentRef.current.paymentMethod;
        const withoutConsent = !acceptRef.current;
        setPaymentMissing(withoutPayment);
        setConsentMissing(withoutConsent);

        if (withoutPayment) {
            const firstOption = paymentElement.current && paymentElement.current.querySelector("input");
            if (firstOption) firstOption.focus();
            return false;
        }
        if (withoutConsent) {
            if (consentElement.current) consentElement.current.focus();
            return false;
        }
        emitInternalEvent("cart_payment_confirmed", {selected_pmt_method: paymentRef.current.paymentProvider});
        return true;
    }, []);

    const handlePayClicked = () => {
        if (!canPay()) return;
        setIsSubmitted(true);
        submitOrder();
    }

	const renderShippingAddress = () => {
		if (adress.shippingAdress) {
			return (
				<div className={"hpm-adressBox"}>
					<span className="hpm-title">{t("cart.personalInformation.shippingAdress")}</span>
					<span
						className={"hpm-edit"}
						onClick={() => props.editBillingAddress()}
						onKeyDown={e => {
							if (e.key === "Enter" || e.key === " ") {
                                props.editBillingAddress();
							}
						}}
						role="button"
						tabIndex={0}
					>
                        <Icon icon={SquarePen} size={22} className="hpm-editIcon"/>
                        {t("cart.change")}
                    </span>
					<table>
						<tbody>
							<tr>
								<td>{adress.shippingName} {adress.shippingFamilyname}</td>
							</tr>
							<tr>
								<td>{adress.shippingStreet}</td>
							</tr>
							<tr>
								<td>{adress.shippingPostalCode} {adress.shippingCity}</td>
							</tr>
						</tbody>
					</table>
				</div>
			);
		}
	}

    // The methods to choose from: PayPal Express is the cart's, cash takes a pickup or a mounting, and
    // PayPal's own only where PayPal offers them to this customer.
    const selectableMethods = paymentMethods
        .filter(method => method.paymentMethod !== PAYMENT_METHODS.PAYPAL_EXPRESS)
        .filter(method => method.paymentMethod !== PAYMENT_METHODS.CASH || !isAllShipping)
        .filter(method => !isPayPalProvider(method.paymentProvider) || isPaymentMethodAvailable(method.paymentMethod));

    const renderPaymentChoice = () => {
        if (isPaymentFixed) {
            return null;
        }
        // while a method is assigned the facility is empty: the one clicked stays chosen
        const chosen = paymentFacility.paymentMethod || chosenMethod;

        return <fieldset
            className={classNames("hpm-paymentChoice", {"hpm-missing": paymentMissing})}
            ref={paymentElement}
            aria-describedby={paymentMissing ? "hpm-paymentChoice-error" : undefined}
        >
            <legend className="hpm-title">{t("cart.paymentMethods.paymentMethodCTA")}</legend>
            {selectableMethods.map(method => {
                const failed = paymentErrors[method.paymentMethod] === true;
                const isChosen = chosen === method.paymentMethod && !failed;
                const hint = isChosen && paymentMethodHint(method.paymentMethod);

                return <label
                    key={method.paymentMethod}
                    className={classNames("hpm-paymentChoice-option", {"hpm-chosen": isChosen, "hpm-failed": failed})}
                >
                    <input
                        type="radio"
                        name="hpm-paymentMethod"
                        value={method.paymentMethod}
                        checked={isChosen}
                        disabled={failed || isAssigningPayment}
                        onChange={() => choosePaymentMethod(method)}
                    />
                    <span className="hpm-paymentChoice-text">
                        {t(`cart.paymentMethods.${method.paymentMethod}`)}
                        {hint && <small>{t(hint)}</small>}
                    </span>
                </label>;
            })}
            {paymentMissing &&
                <p id="hpm-paymentChoice-error" className="hpm-fieldError" role="alert">
                    {t("cart.paymentMethods.paymentRequired")}
                </p>
            }
            {Object.keys(paymentErrors).length > 0 &&
                <InlineNotice kind="error">{t("cart.paymentMethods.assignPaymentError")}</InlineNotice>
            }
        </fieldset>;
    };

    // The button of the chosen method - PayPal's own for its methods; ours for the rest, and as long as
    // none is chosen, so that it says what is missing.
    const renderPayButton = () => {
        if (isAssigningPayment) {
            return <Loading height={48}/>;
        }
        const method = paymentFacility.paymentMethod;
        switch (method) {
            case PAYMENT_METHODS.PRZELEWY24:
                return (
                    <P24PayPalButtonContainer canPay={canPay}/>
                );
            case PAYMENT_METHODS.PAY_LATER:
            case PAYMENT_METHODS.PAYPAL:
            case PAYMENT_METHODS.CARD:
            case PAYMENT_METHODS.SEPA:
                return (
                    <PayPalButtonContainer fundingSource={getFundingSource(method)} canPay={canPay}/>
                );
            default:
                return (
                    <NextButton onClick={handlePayClicked}>
                        {t("cart.calculation.submit")}
                    </NextButton>
                );
        }
    }

    const modalStyles = {
        overlay: {
            zIndex: "100000"
        },
        content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid grey",
            width: "calc(90%)",
            maxWidth: "1000px",
            minHeight: "200px",
            maxHeight: "90vh",
            overflowY: "auto",
        }
    };

    const downloadButtonStyles = {
        display: "flex",
        justifyContent: "end",
        marginRight: "3em"
    };

    const isLoading = () => {

        // the order is created as the address is saved; there is nothing to pay for before
        if (!orderIsCreated) { return true}

        // some complex logic due to different checkout processes
        // Vorkasse, Abholung, PayPalExpress: when pressed the Pay-Button, it is loading (waiting for /accept to return)
        if(isSubmitted) { return true}

        // PayPal, P24 (excl. PayPalExpress), order is pending or readyToSubmit, means it will be automatically submitted
        // when polling the order state returns "authorized", so it is loading
        if(isPayPalPayment() && (isOrderPending() || isOrderReadyToSubmit())) { return true}
    };

    if(isLoading()) {
        return (
            <React.Fragment>
                {orderIsCreated &&
                    <div className={"hpm-loadingMessage"}>
                        <h3>{t("cart.checkingOrder")}</h3>
                    </div>
                }
                <Loading height={100} />
            </React.Fragment>
        );
    }

    return (
        <div className={"hpm-orderSummary"}>
            <div className={"hpm-cartContainer"}>
                <div className="hpm-mainContentBox">
                    <div className="hint" dangerouslySetInnerHTML={{ __html: t(paymentHint(paymentFacility.paymentMethod)) }}></div>
                    <div className={"hpm-cartTitle"}>{t("cart.cartLabel")}</div>
                    <ul className="hpm-cart">
                        {
                            items.map((item, index) => {
                                return <CartItemOverviewContainer key={index} cartItem={item}/>;
                            })
                        }
                    </ul>
                    <div className="hpm-box hpm-shipping">
                        <span
                            className="hpm-title">{t("cart.expectedDeliveryDate.expectedDeliveryDateLabel")}</span>
                        <table>
                            <tbody>
                                <tr>
                                    <td>{shippingText}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {showPickupLocation &&
                        <div className="hpm-box">
                            <span className="hpm-title">
                                {withPickup ? t("cart.articlePickupLocation") : t("cart.articleMountLocation")}
                            </span>
                            <div>
                                <PickupLocationInfoContainer/>
                            </div>
                        </div>
                    }
                    {euShop &&
                        <div className="hpm-box hpm-warrantyRights" data-testid="warranty-rights">
                            <button
                                type="button"
                                onClick={toggleWarrantyNotice}
                            >
                                {t("warranty.notice.checkoutLink")}
                            </button>
                        </div>
                    }
                    <WarrantyNoticeModal isOpen={showWarrantyNotice} onRequestClose={toggleWarrantyNotice} t={t}/>
                    {legalTerms &&
                        <div className="hpm-box hpm-legalTerms">
                            {t("cart.calculation.legalTerms.infoTextPartOne")}
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                    toggleAGB();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleAGB();
                                    }
                                }}
                            >
                                {t("cart.calculation.legalTerms.linkText")}
                            </a>
                            {t("cart.calculation.legalTerms.infoTextPartTwo")}
                        </div>
                    }
                    <Modal isOpen={showLegalTerms}
                           style={modalStyles}
                           shouldCloseOnOverlayClick={true}
                           onRequestClose={toggleAGB}
                    >
                        <button type={"button"} onClick={toggleAGB} className="hpm-modalClose"><Icon icon={X} size={24}/></button>
                        <div style={downloadButtonStyles}>
                            <a className={"file-download-button"}
                               download={t("cart.calculation.legalTerms.fileName")}
                               id="file-download-button"
                               href={`${notificationUrl}/download/legalTerms/${dealerId}`}>{t("cart.calculation.legalTerms.button")}
                            </a>
                        </div>
                        <div dangerouslySetInnerHTML={{__html: legalTerms}}
                             className="hpm-modalContent" style={{fontFamily: "var(--font100)"}}/>
                    </Modal>
                </div>

                <div className="hpm-dataSummaryBox">
                    {!isPayPalExpressPayment() && // PP Express: we have no address data
                    <div className="hpm-box">
                        <div className={"hpm-adressBox"}>
                            <span
                                className="hpm-title">{t("cart.personalInformation.billingAdress")}</span>
                            <span
                                className={"hpm-edit"}
                                onClick={() => props.editBillingAddress()}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        props.editBillingAddress();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                <Icon icon={SquarePen} size={22} className="hpm-editIcon"/>
                                {t("cart.change")}
                            </span>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>
                                            {adress.name} {adress.familyname}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            {adress.street}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            {adress.postalCode} {adress.city}
                                        </td>
                                    </tr>
                                    {adress.phone
                                        ? <tr>
                                            <td>✆ {adress.phone}</td>
                                        </tr> : null
                                    }
                                    {adress.email
                                        ? <tr>
                                            <td>✉ {adress.email}</td>
                                        </tr> : null
                                    }
                                </tbody>
                            </table>
                        </div>
                        {renderShippingAddress()}
                    </div>}
                    <div className={"hpm-dataSummaryBoxContent"}>
                        <PriceOverviewContainer hideSlyCoupon={true} />
                        {renderPaymentChoice()}
                        <div className="hpm-menu">
                            <div
                                className={classNames("hpm-menuElement hpm-conditions", {"hpm-missing": consentMissing})}
                                onClick={() => toggleAccept()}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleAccept();
                                    }
                                }}
                                role="checkbox"
                                aria-checked={accept || false}
                                aria-invalid={consentMissing || undefined}
                                aria-describedby={consentMissing ? "hpm-consent-error" : undefined}
                                tabIndex={0}
                                ref={consentElement}
                            >
                                <div className={accept ? "hpm-circle hpm-selected" : "hpm-circle"}>
                                    {accept && <Icon icon={Check} size={18}/>}
                                </div>
                                <div className="hpm-optionText">
                                    {t("cart.calculation.consent")}
                                </div>
                            </div>
                            {consentMissing &&
                                <p id="hpm-consent-error" className="hpm-fieldError" role="alert">
                                    {t("cart.calculation.consentRequired")}
                                </p>
                            }
                        </div>
                        <div className={"hpm-dataSummaryBoxButtons"}>
                            {paymentAuthorizationFailed &&
                                <InlineNotice kind="error">{t("cart.paymentApiError")}</InlineNotice>
                            }
                            {renderPayButton()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSummary;
